import { useState, useEffect, useCallback, useRef } from 'react';
import { api, DfeDefinitionSummary, DfeDefinitionListResult } from '@/lib/api';

interface UseItemDefinitionsOptions {
  search?: string;
  fungibility?: string;
  pageSize?: number;
}

interface UseItemDefinitionsReturn {
  definitions: DfeDefinitionSummary[];
  total: number;
  loading: boolean;
  error: string | null;
  refresh: () => void;
  loadMore: () => void;
  hasMore: boolean;
  page: number;
  totalPages: number;
  goToPage: (page: number) => void;
}

export function useItemDefinitions(
  options: UseItemDefinitionsOptions = {},
): UseItemDefinitionsReturn {
  const [definitions, setDefinitions] = useState<DfeDefinitionSummary[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [offset, setOffset] = useState(0);
  const limit = options.pageSize || 20;
  const fetchIdRef = useRef(0);

  const fetchDefinitions = useCallback(
    async (newOffset: number = 0, append: boolean = false) => {
      const id = ++fetchIdRef.current;
      try {
        setLoading(true);
        const result: DfeDefinitionListResult = await api.listDfeDefinitions({
          search: options.search || undefined,
          fungibility: options.fungibility || undefined,
          limit,
          offset: newOffset,
        });
        if (fetchIdRef.current !== id) return;
        if (append) {
          setDefinitions((prev) => [...prev, ...result.definitions]);
        } else {
          setDefinitions(result.definitions);
        }
        setTotal(result.total);
        setOffset(newOffset);
      } catch (err) {
        if (fetchIdRef.current !== id) return;
        setError(err instanceof Error ? err.message : 'Failed to load definitions');
      } finally {
        if (fetchIdRef.current === id) {
          setLoading(false);
        }
      }
    },
    [options.search, options.fungibility, limit],
  );

  const refresh = useCallback(() => {
    setError(null);
    fetchDefinitions(0, false);
  }, [fetchDefinitions]);

  const loadMore = useCallback(() => {
    if (!loading && offset + limit < total) {
      fetchDefinitions(offset + limit, true);
    }
  }, [fetchDefinitions, loading, offset, limit, total]);

  const goToPage = useCallback(
    (page: number) => {
      const newOffset = (page - 1) * limit;
      fetchDefinitions(newOffset, false);
    },
    [fetchDefinitions, limit],
  );

  useEffect(() => {
    refresh();
    return () => {
      fetchIdRef.current++;
    };
  }, [refresh]);

  const page = Math.floor(offset / limit) + 1;
  const totalPages = Math.max(1, Math.ceil(total / limit));

  return {
    definitions,
    total,
    loading,
    error,
    refresh,
    loadMore,
    hasMore: offset + limit < total,
    page,
    totalPages,
    goToPage,
  };
}
